import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
    Loader2, RefreshCw,
    ShoppingBag, Landmark, Receipt, ArrowDownUp, RotateCcw, CreditCard,
    DollarSign, TrendingUp,
} from 'lucide-react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend,
    BarChart, Bar,
} from 'recharts';
import { cardAdminApi, CardTransactionStats } from '../../api/cards.api';
import { useToast } from '../../hooks/use-toast';

const formatUSD = (v?: number) =>
    v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '$0.00';

const TYPE_COLORS: Record<string, string> = {
    purchase: '#6366f1',
    atm: '#f59e0b',
    fee: '#ef4444',
    recharge: '#10b981',
    refund: '#8b5cf6',
    reversal: '#64748b',
};

const TYPE_LABELS: Record<string, string> = {
    purchase: 'Acha',
    atm: 'ATM',
    fee: 'Frè',
    recharge: 'Rechaj',
    refund: 'Ranbousman',
    reversal: 'Renvèsman',
};

const STATUS_COLORS: Record<string, string> = {
    completed: '#10b981',
    pending: '#f59e0b',
    declined: '#ef4444',
    reversed: '#8b5cf6',
};

const STATUS_LABELS: Record<string, string> = {
    completed: 'Konplete',
    pending: 'An atant',
    declined: 'Refize',
    reversed: 'Renvèse',
};

const MONTH_LABELS: Record<string, string> = {
    '01': 'Jan', '02': 'Fev', '03': 'Mas', '04': 'Avr',
    '05': 'Me', '06': 'Jen', '07': 'Jiy', '08': 'Aou',
    '09': 'Sep', '10': 'Okt', '11': 'Nov', '12': 'Des',
};

const formatMonth = (m: string) => {
    const parts = m.split('-');
    return `${MONTH_LABELS[parts[1]] || parts[1]} ${parts[0]?.slice(2)}`;
};

export default function VisaDashboardPage() {
    const { toast } = useToast();
    const [stats, setStats] = useState<CardTransactionStats | null>(null);
    const [loading, setLoading] = useState(true);

    const loadStats = useCallback(async () => {
        try {
            setLoading(true);
            const data = await cardAdminApi.getCardTransactionStats();
            setStats(data);
        } catch {
            toast({ title: 'Erè', description: 'Pa kapab chaje statistik yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadStats(); }, [loadStats]);

    const ov = stats?.overview;

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">

                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem><BreadcrumbLink>Kat VISA</BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem><BreadcrumbPage>Dashboard</BreadcrumbPage></BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Dashboard Tranzaksyon Kat</h1>
                            <p className="text-slate-500 text-sm mt-1">Apèsi jeneral sou tout tranzaksyon kat VISA HyperPay.</p>
                        </div>
                        <Button variant="outline" size="sm" className="gap-2 border-slate-200 self-start" onClick={loadStats}>
                            <RefreshCw className="h-4 w-4" /> Aktyalize
                        </Button>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="h-10 w-10 animate-spin text-primary-500" />
                            <p className="text-slate-500 ml-4">Ap chaje statistik yo...</p>
                        </div>
                    ) : stats ? (
                        <>
                            {/* ── Overview Stats Cards ───────────────────────────── */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                                {[
                                    { 
                                        label: 'Total Tranzaksyon', 
                                        value: ov?.totalTransactions ?? 0, 
                                        icon: ArrowDownUp, 
                                        colorClass: 'bg-slate-50 text-slate-700 border-slate-100', 
                                        borderClass: 'border-l-slate-400',
                                        textGradient: 'from-slate-700 to-slate-900',
                                        desc: 'Aktivite jeneral'
                                    },
                                    { 
                                        label: 'Acha', 
                                        value: ov?.totalPurchase ?? 0, 
                                        icon: ShoppingBag, 
                                        colorClass: 'bg-indigo-50 text-indigo-600 border-indigo-100', 
                                        borderClass: 'border-l-indigo-500',
                                        textGradient: 'from-indigo-600 to-blue-800',
                                        desc: 'Acha machann'
                                    },
                                    { 
                                        label: 'ATM', 
                                        value: ov?.totalATM ?? 0, 
                                        icon: Landmark, 
                                        colorClass: 'bg-amber-50 text-amber-600 border-amber-100', 
                                        borderClass: 'border-l-amber-500',
                                        textGradient: 'from-amber-500 to-orange-700',
                                        desc: 'Retrè lajan'
                                    },
                                    { 
                                        label: 'Frè', 
                                        value: ov?.totalFee ?? 0, 
                                        icon: Receipt, 
                                        colorClass: 'bg-red-50 text-red-600 border-red-100', 
                                        borderClass: 'border-l-red-500',
                                        textGradient: 'from-red-600 to-rose-700',
                                        desc: 'Frè aplikasyon'
                                    },
                                    { 
                                        label: 'Rechaj', 
                                        value: ov?.totalRecharge ?? 0, 
                                        icon: CreditCard, 
                                        colorClass: 'bg-emerald-50 text-emerald-600 border-emerald-100', 
                                        borderClass: 'border-l-emerald-500',
                                        textGradient: 'from-emerald-600 to-teal-700',
                                        desc: 'Depo lajan'
                                    },
                                    { 
                                        label: 'Ranbousman', 
                                        value: ov?.totalRefund ?? 0, 
                                        icon: RotateCcw, 
                                        colorClass: 'bg-violet-50 text-violet-600 border-violet-100', 
                                        borderClass: 'border-l-violet-500',
                                        textGradient: 'from-violet-600 to-purple-700',
                                        desc: 'Lajan ranbouse'
                                    },
                                ].map(({ label, value, icon: Icon, colorClass, borderClass, textGradient, desc }) => (
                                    <Card key={label} className={`border border-slate-200/50 border-l-4 ${borderClass} shadow-none bg-white rounded-lg hover:bg-slate-50/50 transition-colors duration-200`}>
                                        <CardContent className="py-2.5 px-3 flex items-center justify-between min-h-[72px]">
                                            <div className="flex items-center gap-2.5 min-w-0">
                                                <div className={`p-2 rounded-lg ${colorClass.split(' ')[0]} ${colorClass.split(' ')[1]} flex-shrink-0`}>
                                                    <Icon className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-slate-500 text-[10px] sm:text-xs font-bold uppercase tracking-wider leading-none truncate">{label}</p>
                                                    <p className="text-[10px] text-slate-400 italic font-medium mt-0.5 leading-none truncate">{desc}</p>
                                                </div>
                                            </div>
                                            <p className={`text-xl sm:text-2xl font-black font-mono tracking-tight leading-none bg-gradient-to-r ${textGradient} bg-clip-text text-transparent flex-shrink-0 ml-2`}>
                                                {value.toLocaleString()}
                                            </p>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
 
                            {/* ── Financial Summary ─────────────────────────────── */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Card className="border border-slate-200/50 border-l-4 border-l-indigo-500 shadow-none bg-white rounded-lg hover:bg-slate-50/50 transition-colors duration-200">
                                    <CardContent className="py-3 px-4 flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                                            <DollarSign className="h-5 w-5 text-indigo-600" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider leading-none">Total Depanse (Acha + ATM)</p>
                                            <p className="text-xl sm:text-2xl font-black bg-gradient-to-r from-indigo-600 to-blue-700 bg-clip-text text-transparent font-mono mt-1 leading-none">{formatUSD(ov?.totalSpentUSD)}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                                <Card className="border border-slate-200/50 border-l-4 border-l-emerald-500 shadow-none bg-white rounded-lg hover:bg-slate-50/50 transition-colors duration-200">
                                    <CardContent className="py-3 px-4 flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
                                            <TrendingUp className="h-5 w-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider leading-none">Total Frè Kolekte</p>
                                            <p className="text-xl sm:text-2xl font-black bg-gradient-to-r from-emerald-600 to-teal-700 bg-clip-text text-transparent font-mono mt-1 leading-none">{formatUSD(ov?.totalFeesUSD ? Math.abs(ov.totalFeesUSD) : 0)}</p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* ── Evolution Chart (Area) ────────────────────────── */}
                            <Card className="border-slate-200 shadow-sm">
                                <CardContent className="pt-6 pb-4">
                                    <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">
                                        Evolisyon Tranzaksyon — 12 Dènye Mwa
                                    </h3>
                                    <div className="h-[320px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={stats.evolution.map(e => ({ ...e, month: formatMonth(e.month) }))} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="gPurchase" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                                    </linearGradient>
                                                    <linearGradient id="gAtm" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                                                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                                                    </linearGradient>
                                                    <linearGradient id="gRecharge" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                                                <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                                                <RechartsTooltip
                                                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)' }}
                                                    labelStyle={{ fontWeight: 700, color: '#1e293b' }}
                                                />
                                                <Area type="monotone" dataKey="purchase" name="Acha" stroke="#6366f1" fillOpacity={1} fill="url(#gPurchase)" strokeWidth={2} />
                                                <Area type="monotone" dataKey="atm" name="ATM" stroke="#f59e0b" fillOpacity={1} fill="url(#gAtm)" strokeWidth={2} />
                                                <Area type="monotone" dataKey="recharge" name="Rechaj" stroke="#10b981" fillOpacity={1} fill="url(#gRecharge)" strokeWidth={2} />
                                                <Area type="monotone" dataKey="fee" name="Frè" stroke="#ef4444" fill="transparent" strokeWidth={2} strokeDasharray="5 5" />
                                                <Area type="monotone" dataKey="refund" name="Ranbousman" stroke="#8b5cf6" fill="transparent" strokeWidth={2} strokeDasharray="3 3" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* ── Pie Chart (by type) + Bar Chart (by status) ──── */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Pie — by type */}
                                <Card className="border-slate-200 shadow-sm">
                                    <CardContent className="pt-6 pb-4">
                                        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">
                                            Distribisyon pa Tip
                                        </h3>
                                        <div className="h-[280px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={stats.byType.map(t => ({
                                                            name: TYPE_LABELS[t.type] || t.type,
                                                            value: t.count,
                                                        }))}
                                                        cx="50%" cy="50%"
                                                        innerRadius={60} outerRadius={100}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                        strokeWidth={2}
                                                        stroke="#fff"
                                                    >
                                                        {stats.byType.map((t, i) => (
                                                            <Cell key={i} fill={TYPE_COLORS[t.type] || '#94a3b8'} />
                                                        ))}
                                                    </Pie>
                                                    <Legend
                                                        verticalAlign="bottom"
                                                        iconType="circle"
                                                        wrapperStyle={{ fontSize: 12, color: '#64748b' }}
                                                    />
                                                    <RechartsTooltip
                                                        contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                                                        formatter={(value: number, name: string) => [`${value} tranzaksyon`, name]}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Bar — by status */}
                                <Card className="border-slate-200 shadow-sm">
                                    <CardContent className="pt-6 pb-4">
                                        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">
                                            Distribisyon pa Estati
                                        </h3>
                                        <div className="h-[280px]">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart
                                                    data={stats.byStatus.map(s => ({
                                                        name: STATUS_LABELS[s.status] || s.status,
                                                        count: s.count,
                                                        fill: STATUS_COLORS[s.status] || '#94a3b8',
                                                    }))}
                                                    layout="vertical"
                                                    margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                                                    <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} />
                                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 13, fill: '#334155', fontWeight: 600 }} width={80} />
                                                    <RechartsTooltip
                                                        contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }}
                                                        formatter={(value: number) => [`${value} tranzaksyon`]}
                                                    />
                                                    <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={28}>
                                                        {stats.byStatus.map((s, i) => (
                                                            <Cell key={i} fill={STATUS_COLORS[s.status] || '#94a3b8'} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center justify-center py-20">
                            <p className="text-slate-400">Okenn done pa disponib.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
